/**
 * 运镜库 · 新建自定义运镜方案弹窗
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import type { CameraMoveStep } from './types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated?: () => void;
}

const SHOT_TYPE_OPTIONS = ['大远景', '远景', '全景', '中景', '近景', '特写', '大特写'];
const CAMERA_MOVE_OPTIONS = [
  '固定', '缓慢推近', '急推', '拉远', '横移', '跟拍', '环绕',
  '缓慢环绕', '加速环绕', '骤停固定', '升降', '俯拍急推', '仰拍上升',
  '穿越下沉', '手持晃动',
];
const SCENE_TAG_OPTIONS = [
  '角色登场', '旅途出发', '决心表达', '情绪递进',
  '悬念揭晓', '内心独白', '情绪爆发', '角色顿悟',
  '坠落', '重生', '时空穿越', '梦境转换',
  '追逐', '对峙', '告别', '重逢',
];

function emptyStep(stepNo: number): CameraMoveStep {
  return { stepNo, shotType: '中景', cameraMovement: '固定', notes: '' };
}

export function CreateCameraMoveDialog({ open, onOpenChange, onCreated }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<CameraMoveStep[]>([emptyStep(1), emptyStep(2)]);
  const [tags, setTags] = useState<string[]>([]);

  const reset = () => {
    setName('');
    setDescription('');
    setSteps([emptyStep(1), emptyStep(2)]);
    setTags([]);
  };

  const createMut = useMutation({
    mutationFn: async () => {
      return await api
        .post('brain/assets', {
          json: {
            kind: 'camera_move',
            name: name.trim(),
            attributes: {
              description: description.trim(),
              movementSequence: steps.map((s, i) => ({ ...s, stepNo: i + 1 })),
              suitableScenes: tags,
              isPreset: false,
            },
            referenceImages: [],
          },
        })
        .json();
    },
    onSuccess: () => {
      toast.success('运镜方案创建成功');
      reset();
      onOpenChange(false);
      onCreated?.();
    },
    onError: (e: Error) => toast.error('创建失败', e.message || '网络异常'),
  });

  const updateStep = (idx: number, patch: Partial<CameraMoveStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeStep = (idx: number) => {
    if (steps.length <= 2) return;
    setSteps((prev) => prev.filter((_, i) => i !== idx));
  };

  const addStep = () => {
    setSteps((prev) => [...prev, emptyStep(prev.length + 1)]);
  };

  const toggleTag = (tag: string) => {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const canSubmit = name.trim().length >= 2 && steps.length >= 2;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
      title="新建运镜方案"
      className="max-w-xl"
    >
      <div className="space-y-5 py-2">
        {/* 名称 */}
        <div>
          <label className="mb-1 block text-sm font-medium">方案名称</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="如：追逐三段式"
            maxLength={30}
          />
        </div>

        {/* 描述 */}
        <div>
          <label className="mb-1 block text-sm font-medium">描述</label>
          <textarea
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="简述运镜思路与适用场景"
            maxLength={200}
          />
        </div>

        {/* 步骤序列 */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium">运镜步骤</label>
            <Button size="sm" variant="ghost" onClick={addStep}>
              <Plus className="mr-1 h-3 w-3" />
              添加步骤
            </Button>
          </div>
          <div className="space-y-2">
            {steps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-2 rounded border bg-neutral-50 p-2">
                <GripVertical className="h-4 w-4 shrink-0 text-neutral-300" />
                <span className="shrink-0 text-xs font-medium text-neutral-500">
                  {idx + 1}
                </span>
                <select
                  className="rounded border px-2 py-1 text-xs"
                  value={step.shotType}
                  onChange={(e) => updateStep(idx, { shotType: e.target.value })}
                >
                  {SHOT_TYPE_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <select
                  className="rounded border px-2 py-1 text-xs"
                  value={step.cameraMovement}
                  onChange={(e) => updateStep(idx, { cameraMovement: e.target.value })}
                >
                  {CAMERA_MOVE_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <Input
                  className="h-7 flex-1 text-xs"
                  value={step.notes}
                  onChange={(e) => updateStep(idx, { notes: e.target.value })}
                  placeholder="备注"
                />
                <button
                  type="button"
                  className="shrink-0 rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-30"
                  disabled={steps.length <= 2}
                  onClick={() => removeStep(idx)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* 适用场景 tags */}
        <div>
          <label className="mb-1 block text-sm font-medium">适用场景</label>
          <div className="flex flex-wrap gap-1.5">
            {SCENE_TAG_OPTIONS.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                  tags.includes(tag)
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                }`}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            disabled={!canSubmit || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? '创建中…' : '创建'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
